import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { api, formatApiError } from "../../api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { toast } from "../ui/sonner";
import { CONTROL } from "../../constants/testIds";
import { fadeUp } from "../../lib/motion";

// UX-CP-01 §3 SCR-P08-15 "Suspend/restore — sets read_only (with a grace note) / restore active.
// Type-to-confirm the gym's exact name. Never a destructive action — nothing is deleted, the
// gym's own data is untouched, only license.state flips."
export function SuspendRestorePanel({ orgId, displayName, licenseState, canEdit }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const reducedMotion = useReducedMotion();
  const queryClient = useQueryClient();
  const isSuspended = licenseState === "read_only";

  const stateMutation = useMutation({
    mutationFn: (state) => api.patch(`/control/license/${orgId}/state`, { state }),
    onSuccess: (_res, state) => {
      toast.success(state === "read_only" ? `${displayName} suspended` : `${displayName} restored`);
      queryClient.invalidateQueries({ queryKey: ["control-gym-detail", orgId] });
      queryClient.invalidateQueries({ queryKey: ["control-gyms"] });
      setConfirmOpen(false);
      setConfirmText("");
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  if (!isSuspended) {
    return (
      <motion.div {...fadeUp(reducedMotion)} className="solid-surface rounded-[14px] p-5 space-y-3">
        <p className="eyebrow">Suspend this gym</p>
        <p className="text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>
          Sets the license to read-only. The gym's data stays exactly as it is — staff simply lose write access until restored.
        </p>
        <Button
          data-testid={CONTROL.suspendButton}
          disabled={!canEdit}
          onClick={() => setConfirmOpen(true)}
          variant="outline"
          style={{ color: "var(--scarlet-neg)", borderColor: "var(--scarlet-neg)" }}
        >
          Suspend gym
        </Button>
        {!canEdit && <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>Requires the 'billing' Kairoo permission.</p>}

        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent data-testid={CONTROL.suspendConfirmDialog}>
            <AlertDialogHeader>
              <AlertDialogTitle>Suspend {displayName}?</AlertDialogTitle>
              <AlertDialogDescription>
                Type <strong>{displayName}</strong> to confirm. This sets the license to read-only — reversible any time via Restore.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <Input data-testid={CONTROL.suspendConfirmInput} value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={displayName} />
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setConfirmText("")}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                data-testid={CONTROL.suspendConfirmSubmit}
                disabled={confirmText !== displayName || stateMutation.isPending}
                onClick={() => stateMutation.mutate("read_only")}
                className="text-white"
                style={{ background: "var(--scarlet-neg)" }}
              >
                Suspend
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </motion.div>
    );
  }

  return (
    <motion.div {...fadeUp(reducedMotion)} className="solid-surface rounded-[14px] p-5 space-y-3">
      <p className="eyebrow">Restore this gym</p>
      <p className="text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>This gym is currently suspended (read-only). Restoring sets its license back to active immediately.</p>
      <Button
        data-testid={CONTROL.restoreButton}
        disabled={!canEdit || stateMutation.isPending}
        onClick={() => stateMutation.mutate("active")}
        className="text-white"
        style={{ background: "var(--scarlet-accent)" }}
      >
        Restore gym
      </Button>
      {!canEdit && <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>Requires the 'billing' Kairoo permission.</p>}
    </motion.div>
  );
}
